/**
 * Who is signed in, for the whole app.
 *
 * One fetch of `/auth/session` plus `/workspace/profile` at mount, exposed
 * through context. The three states are explicit — `loading`, `anonymous`,
 * `authenticated` — because the difference between "we do not know yet" and
 * "nobody is signed in" is the difference between showing a spinner and
 * redirecting to the sign-in page, and a boolean collapses it.
 *
 * The profile travels with the session on purpose: the capability mirror
 * (`visibleTaskTypes`, `mayAdministerTasks`) and the onboarding flag are
 * session facts, and a component that could see a session without them would
 * be tempted to infer capabilities from the role string — which is exactly
 * the client-side role list this design forbids.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  fetchProfile,
  fetchSession,
  logout as apiLogout,
  onSessionUnauthorized,
  type SessionAccount,
  type WorkspaceProfile,
} from './api.js';

export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'anonymous' }
  | {
      readonly status: 'authenticated';
      readonly account: SessionAccount;
      readonly profile: WorkspaceProfile;
    };

interface SessionValue {
  readonly session: SessionState;
  /** Re-read the session and profile — after onboarding, after admin acts. */
  readonly refresh: () => Promise<void>;
  readonly logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const [session, setSession] = useState<SessionState>({ status: 'loading' });

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const info = await fetchSession();
      if (info === null) {
        setSession({ status: 'anonymous' });
        return;
      }
      if (info.account.role === 'MERCHANT') {
        // Merchant identity is intentionally outside the contributor
        // workspace. Do not probe `/workspace/profile` or infer internal
        // capabilities from the shared browser session.
        setSession({
          status: 'authenticated',
          account: info.account,
          profile: {
            visibleTaskTypes: [],
            mayAdministerTasks: false,
            mayDeleteDataRoomFolders: false,
            mayUseDataRoom: false,
            onboardingCompleted: true,
          },
        });
        return;
      }
      const profile = await fetchProfile();
      setSession({ status: 'authenticated', account: info.account, profile });
    } catch {
      // A failed bootstrap is treated as anonymous rather than as an error
      // page: the sign-in screen is reachable and will surface the problem
      // if it persists. Nothing authenticated is rendered on a guess.
      setSession({ status: 'anonymous' });
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout();
    } catch {
      // The server may already consider the session gone. Local state is
      // cleared either way; what must never happen is the UI staying
      // "signed in" after the person asked to leave.
    }
    setSession({ status: 'anonymous' });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The centralised dead-session path: any 401 outside /auth/session flips
  // the state to anonymous immediately, so the guard redirects to sign-in
  // (preserving the location) and the sign-in page cannot bounce back on a
  // stale "authenticated". Expiry, revocation, suspension and
  // deauthorization all arrive through here — the server keeps them
  // indistinguishable on purpose.
  useEffect(() => {
    onSessionUnauthorized(() => {
      setSession({ status: 'anonymous' });
    });
    return () => {
      onSessionUnauthorized(null);
    };
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ session, refresh, logout }),
    [session, refresh, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error('useSession must be used inside a SessionProvider.');
  }
  return value;
}
